import { default as axios } from 'axios';
import debounce from 'lodash/debounce';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { ref, toValue } from 'vue';

import api from '@/api';
import { StopMileageResponse } from '@/model';
import {
  ProposedShipment,
  RouteBuilderMileageState,
} from '@/model/routeBuilder';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2/RoutePlanProposedShipment';

export type Entity = ProposedShipment | RoutePlanProposedShipment;
export type Identifier = Entity['identifier'];
export type Mileages = Record<Identifier, RouteBuilderMileageState>;
export interface UseMileageResponse {
  clearMileage: (identifier: Identifier) => void;
  loadMileage: (identifier: Identifier) => void;
  loadMileageForAll: (identifiers: Identifier[]) => void;
  mileages: Ref<Mileages>;
}

export const useMileage = (
  entities: MaybeRefOrGetter<Entity[]>,
): UseMileageResponse => {
  const entries: Array<[Identifier, RouteBuilderMileageState]> = toValue(
    entities,
  ).map((entity) => [entity.identifier, { response: { loading: false } }]);
  const mileages = ref(Object.fromEntries(entries));

  const loadMileage = (identifier: Identifier) => {
    const entity = toValue(entities).find((e) => e.identifier === identifier);
    if (!entity) {
      throw new Error(`Could not find proposed shipment ${identifier}`);
    }

    const mileage = mileages.value[identifier] ?? {
      response: { loading: true },
    };
    mileages.value[identifier] = mileage;

    mileage.response = { loading: true };

    if (mileage.cancelToken) {
      loadMileageDebounced.cancel();
      mileage.cancelToken.cancel();
    }
    mileage.cancelToken = axios.CancelToken.source();

    api.route
      .findMileage(entity.stops, mileage.cancelToken)
      .then((response) => {
        mileage.response.data = response.map(
          (r) =>
            new StopMileageResponse({
              ...r,
              shipmentIdentifier: entity.identifier,
            }),
        );
      })
      .catch((e) => {
        console.error('Error fetching mileage', e);
        mileage.response.error = e;
      })
      .finally(() => {
        mileage.response.loading = false;
        mileage.cancelToken = undefined;
        mileages.value = { ...mileages.value };
      });
  };
  const loadMileageDebounced = debounce(loadMileage, 1000);

  const loadMileageForAll = (identifiers: Identifier[]) => {
    identifiers.forEach((identifier) => loadMileage(identifier));
  };

  const clearMileage = (identifier: Identifier) => {
    mileages.value[identifier].response = { loading: false };
  };

  return {
    clearMileage,
    loadMileage: loadMileageDebounced,
    loadMileageForAll,
    mileages,
  };
};
